import { useCallback, useEffect, useState } from 'react';

import { api, type ElmaradoLista } from '../api.ts';
import { ft } from '../formazas.ts';
import { AlfulValto } from '../reszek/AlfulValto.tsx';
import { SzolgaltatasMatrix } from '../reszek/SzolgaltatasMatrix.tsx';

/**
 * Elmaradó partnerek. (Megbízás 2.0 / 5.10)
 *
 * A kiválasztott hónap az előzőhöz mérve: ki hagyott fel a vásárlással, ki
 * esett vissza, ki jött vissza. Ez az a kimutatás, amiből kiderül, hova kell
 * telefonálni — a Napi bontás megmutatja, hogy ki nem vásárolt, ez viszont
 * azt, hogy **kinél változott meg valami**.
 *
 * Csak a lezárt napok forgalmát nézi: a folyamatban lévő napból még nem
 * szabad visszaesésre következtetni.
 */

const SZURO_SORREND = ['elmaradt', 'visszaeses', 'uj', 'mind'] as const;

/** Az utolsó 24 hónap, a mai hónappal kezdve. */
function honapok(): string[] {
  const lista: string[] = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 24; i++) {
    lista.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return lista;
}

/**
 * Két kimutatás egy képernyőn. (2026-10-08)
 *
 * Az **Elmaradó partnerek** azt mutatja, kinél változott valami hónapról
 * hónapra; a **Szolgáltatások** azt, kinek mije van egyáltalán. A kettő
 * ugyanarra a munkára kell — kit hívjunk fel —, ezért került egy helyre.
 */
type Ful = 'elmarado' | 'szolgaltatas';

export function ElmaradoPartnerek({ onKarton }: { onKarton: (etterem_id: number) => void }) {
  const [ful, setFul] = useState<Ful>('elmarado');
  const valaszthato = honapok();
  const [honap, setHonap] = useState(valaszthato[0]!);
  const [szuro, setSzuro] = useState<string>('elmaradt');
  const [lista, setLista] = useState<ElmaradoLista | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  const betolt = useCallback(async () => {
    try {
      setLista(
        await api.get<ElmaradoLista>(
          `/api/attekintes/elmarado?honap=${honap}&szuro=${szuro}`,
        ),
      );
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    }
  }, [honap, szuro]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const sorok = lista?.sorok ?? [];
  const o = lista?.osszesites;

  const valto = (
    <AlfulValto<Ful>
      ertek={ful}
      onValt={setFul}
      fulek={[
        { kulcs: 'elmarado', cimke: 'Elmaradó partnerek' },
        { kulcs: 'szolgaltatas', cimke: 'Szolgáltatások' },
      ]}
    />
  );

  if (ful === 'szolgaltatas') {
    return (
      <>
        <div className="dayhead">
          <span style={{ fontSize: 15, fontWeight: 600 }}>Ki milyen szolgáltatást kap</span>
          {valto}
        </div>
        <SzolgaltatasMatrix onKarton={onKarton} />
      </>
    );
  }

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Elmaradó partnerek</span>
        {valto}
        <select value={honap} onChange={(e) => setHonap(e.target.value)}>
          {valaszthato.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
          összehasonlítva ezzel: <b>{lista?.elozo_honap ?? '—'}</b>
        </span>
        <div className="stats">
          <div>
            Elmaradt
            <strong>{ft(o?.elmaradt_db ?? 0)}</strong>
          </div>
          <div>
            Kiesett mennyiség
            <strong>{ft(o?.elmaradt_liter ?? 0)} l</strong>
          </div>
          <div>
            Új vagy visszatért
            <strong>{ft(o?.uj_db ?? 0)}</strong>
          </div>
        </div>
      </div>

      <div className="fchips">
        {SZURO_SORREND.map((k) => (
          <button
            key={k}
            className={`fchip${szuro === k ? ' on' : ''}`}
            onClick={() => setSzuro(k)}
          >
            {lista?.szuro_nevek[k] ?? k}
            <b>{ft(lista?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th>Étterem</th>
              <th className="n" style={{ width: 110 }}>
                {lista?.elozo_honap ?? 'előző'}
              </th>
              <th className="n" style={{ width: 110 }}>
                {honap}
              </th>
              <th className="n" style={{ width: 90 }}>
                Változás
              </th>
              <th className="n" style={{ width: 110 }}>
                Utolsó vásárlás
              </th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.etterem_id}>
                <td className="nm3" onClick={() => onKarton(s.etterem_id)} title="Partnerkarton">
                  <b>{s.nev}</b>
                  {(s.cegnev || s.cim) && <small>{[s.cegnev, s.cim].filter(Boolean).join(' · ')}</small>}
                </td>
                <td className="calc2">{s.elozo_liter ? ft(s.elozo_liter) : '·'}</td>
                <td className="calc2">{s.mostani_liter ? ft(s.mostani_liter) : '·'}</td>
                <td
                  className={`calc2${
                    s.fajta === 'elmaradt' || s.fajta === 'visszaeses' ? ' bad2' : ''
                  }`}
                  style={s.fajta === 'uj' ? { color: 'var(--ok)', fontWeight: 600 } : undefined}
                >
                  {s.fajta === 'uj'
                    ? 'új'
                    : s.fajta === 'elmaradt'
                      ? 'elmaradt'
                      : s.valtozas === null
                        ? '·'
                        : `${s.valtozas > 0 ? '+' : ''}${s.valtozas}%`}
                </td>
                <td className="calc2">{s.utolso_vasarlas ?? '—'}</td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {lista === null
                    ? 'Betöltés…'
                    : 'Nincs ilyen partner ebben a hónapban. Ha még nincs lezárt nap, itt nem lesz adat.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="uzenet flag" style={{ marginTop: 14 }}>
        <b>Csak a lezárt napok számítanak.</b> Ha a hónap még folyamatban van, a „visszaesés"
        természetes — a hónap végén lesz belőle valódi kép.
      </div>
    </>
  );
}
